import React, { useState } from 'react';
import { testFullSellerFlow, testLogin, testBecomeSeller, testCheckAuth, resetAuth, testNavbarState } from '../utils/authTestHelper';
import { useAuth } from '../hooks/useAuth';

const TestSellerFlow: React.FC = () => {
  const [email, setEmail] = useState('testuser@example.com');
  const [password, setPassword] = useState('Test123!');
  const [testLogs, setTestLogs] = useState<string[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [reloadAfterReset, setReloadAfterReset] = useState(false);
  const { user, forceRefreshUserData, checkAuth } = useAuth();

  // Capture console logs for display
  const originalConsoleLog = console.log;
  const originalConsoleError = console.error;
  const captureLog = (message: any, ...args: any[]) => {
    let formattedMessage = message;
    if (typeof message === 'object') {
      formattedMessage = JSON.stringify(message, null, 2);
    }
    const fullMessage = `LOG: ${formattedMessage}`;
    setTestLogs(prev => [...prev, fullMessage]);
    originalConsoleLog(message, ...args);
  };
  const captureError = (message: any, ...args: any[]) => {
    let formattedMessage = message;
    if (typeof message === 'object') {
      formattedMessage = JSON.stringify(message, null, 2);
    }
    const fullMessage = `ERROR: ${formattedMessage}`;
    setTestLogs(prev => [...prev, fullMessage]);
    originalConsoleError(message, ...args);
  };

  // Run the full seller flow test
  const runFullTest = async () => {
    setTestLogs([]);
    setIsRunning(true);
    
    // Override console methods
    console.log = captureLog;
    console.error = captureError;
    
    try {
      await testFullSellerFlow(email, password);
    } catch (error) {
      console.error('Test failed with unexpected error:', error);
    } finally {
      // Check navbar state at the end
      await testNavbarState();
      
      // Restore console methods
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      setIsRunning(false);
    }
  };

  // Run individual tests
  const runLoginTest = async () => {
    setTestLogs([]);
    setIsRunning(true);
    console.log = captureLog;
    console.error = captureError;
    try {
      await testLogin(email, password);
    } finally {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      setIsRunning(false);
    }
  };

  const runBecomeSellerTest = async () => {
    setTestLogs([]);
    setIsRunning(true);
    console.log = captureLog;
    console.error = captureError;
    try {
      await testBecomeSeller();
    } finally {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      setIsRunning(false);
    }
  };

  const runCheckAuthTest = async () => {
    setTestLogs([]);
    setIsRunning(true);
    console.log = captureLog;
    console.error = captureError;
    try {
      await testCheckAuth();
    } finally {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      setIsRunning(false);
    }
  };
  
  const runNavbarTest = async () => {
    setTestLogs([]);
    setIsRunning(true);
    console.log = captureLog;
    console.error = captureError;
    try {
      await testNavbarState();
    } finally {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      setIsRunning(false);
    }
  };

  const runResetAuth = async () => {
    setTestLogs([]);
    setIsRunning(true);
    console.log = captureLog;
    console.error = captureError;
    try {
      console.log('Resetting authentication state...');
      await resetAuth();
      console.log('Auth reset complete - user should be logged out');
      
      // After reset, we don't need to refresh user data since we're logged out
      // Just update the UI to show that user is logged out
      console.log('User is now logged out');
      setTestLogs(prev => [...prev, 'LOG: User is logged out. The navbar should show Login button.']);
      
      // Reload the page if the option is selected
      if (reloadAfterReset) {
        console.log('Reloading page to update UI...');
        setTestLogs(prev => [...prev, 'LOG: Reloading page to refresh UI...']);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        console.log('To see changes in the navbar, you may need to reload the page manually');
      }
    } catch (error) {
      console.error('Error during auth reset:', error);
    } finally {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      setIsRunning(false);
    }
  };

  // Manual force refresh
  const forceRefresh = async () => {
    setTestLogs(prev => [...prev, 'LOG: Manually forcing refresh of auth state...']);
    
    // Check if a token exists first
    const token = localStorage.getItem('token');
    if (!token) {
      setTestLogs(prev => [
        ...prev, 
        'ERROR: No token found - user is not logged in',
        'LOG: Nothing to refresh because user is not authenticated',
        'LOG: Try logging in first using the "Test Login" button'
      ]);
      return;
    }
    
    try {
      // Since a token exists, we can proceed with the refresh
      await forceRefreshUserData();
      await new Promise(resolve => setTimeout(resolve, 100));
      await checkAuth();
      
      setTestLogs(prev => [
        ...prev, 
        `LOG: Auth refreshed successfully`,
        `LOG: Current user: ${user ? JSON.stringify({
          id: user.id,
          username: user.username,
          role: user.role
        }, null, 2) : 'null'}`
      ]);
      
      // Reload page to ensure navbar updates
      setTestLogs(prev => [...prev, 'LOG: Reloading page to refresh UI...']);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      setTestLogs(prev => [...prev, `ERROR: Failed to refresh auth state: ${error.message}`]);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Seller Flow Test</h1>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Test Credentials</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>
        
        <div className="flex items-center mb-4">
          <input
            id="reload-after-reset"
            type="checkbox"
            checked={reloadAfterReset}
            onChange={(e) => setReloadAfterReset(e.target.checked)}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
          <label htmlFor="reload-after-reset" className="ml-2 text-sm text-gray-700">
            Reload page after logout
          </label>
        </div>
        
        <div className="flex flex-wrap gap-3 mt-6">
          <button
            onClick={runFullTest}
            disabled={isRunning}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
          >
            Run Full Test
          </button>
          <button
            onClick={runLoginTest}
            disabled={isRunning}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300"
          >
            Test Login
          </button>
          <button
            onClick={runBecomeSellerTest}
            disabled={isRunning}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-purple-300"
          >
            Test Become Seller
          </button>
          <button
            onClick={runCheckAuthTest}
            disabled={isRunning}
            className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-yellow-300"
          >
            Test Auth State
          </button>
          <button
            onClick={runNavbarTest}
            disabled={isRunning}
            className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 disabled:bg-pink-300"
          >
            Test Navbar State
          </button>
          <button
            onClick={runResetAuth}
            disabled={isRunning}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-300"
          >
            Reset Auth
          </button>
          <button
            onClick={forceRefresh}
            disabled={isRunning}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-300"
          >
            Force Refresh UI
          </button>
        </div>
      </div>
      
      <div className="bg-gray-800 rounded-lg shadow-md p-4 text-white font-mono">
        <h2 className="text-xl font-semibold mb-4 border-b border-gray-700 pb-2">Test Logs</h2>
        <div className="overflow-auto h-96">
          {testLogs.map((log, index) => (
            <div 
              key={index} 
              className={`mb-1 text-sm ${log.startsWith('ERROR') ? 'text-red-400' : 'text-green-300'}`}
            >
              {log}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TestSellerFlow; 